/**
 * MarqueeSelection - 框选组件
 * 复刻 trial_demo_editor 的 MarqueeSelection.jsx
 * 在背景区域按下鼠标拖出选择框，通过 AABB 碰撞检测选中 block
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

interface SelectionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MarqueeSelectionProps {
  onSelectionChange: (ids: string[]) => void;
}

export function MarqueeSelection({ onSelectionChange }: MarqueeSelectionProps) {
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const isDragging = useRef(false);
  const startPoint = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);

  const checkCollision = useCallback((boxRect: { left: number; top: number; right: number; bottom: number }) => {
    const blocks = document.querySelectorAll('[data-block-id]');
    const selectedIds: string[] = [];

    blocks.forEach(el => {
      const rect = el.getBoundingClientRect();
      if (
        rect.left < boxRect.right &&
        rect.right > boxRect.left &&
        rect.top < boxRect.bottom &&
        rect.bottom > boxRect.top
      ) {
        const id = el.getAttribute('data-block-id');
        if (id) selectedIds.push(id);
      }
    });

    onSelectionChange(selectedIds);
  }, [onSelectionChange]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      // 如果点击的是拖拽手柄，不启动框选
      if ((e.target as HTMLElement).closest('[data-drag-handle]')) return;

      // 如果点击在 block 内部（包括 contentEditable、bullet 等），不启动框选
      if ((e.target as HTMLElement).closest('[data-block-id]')) return;

      // 只有点击背景区域才触发
      const target = e.target as HTMLElement;
      if (target.dataset.isBackground) {
        isDragging.current = true;
        startPoint.current = { x: e.clientX, y: e.clientY };
        setSelectionBox({ x: e.clientX, y: e.clientY, width: 0, height: 0 });
        onSelectionChange([]);
        e.preventDefault();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;

      const currentX = e.clientX;
      const currentY = e.clientY;
      const startX = startPoint.current.x;
      const startY = startPoint.current.y;

      const x = Math.min(startX, currentX);
      const y = Math.min(startY, currentY);
      const width = Math.abs(currentX - startX);
      const height = Math.abs(currentY - startY);

      setSelectionBox({ x, y, width, height });

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        checkCollision({ left: x, top: y, right: x + width, bottom: y + height });
      });
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        setSelectionBox(null);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      }
    };

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onSelectionChange, checkCollision]);

  if (!selectionBox) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: selectionBox.x,
        top: selectionBox.y,
        width: selectionBox.width,
        height: selectionBox.height,
        backgroundColor: 'rgba(35, 131, 226, 0.1)',
        border: '1px solid rgba(35, 131, 226, 0.5)',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />
  );
}

export default MarqueeSelection;
